import * as mutations from "../../graphql/mutations";
import AddIcon from "@mui/icons-material/Add";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import DeleteForeverIcon from "@mui/icons-material/DeleteForever";
import { apiClient } from "../../helpers/apiClient";
import { v4 as uuid } from "uuid";
import { useTranslation } from "react-i18next";
import { useEffect, useState } from "react";
import { packages, CGI_BLACK } from "../../helpers/enums";
import {
  fetchAssessmentById,
  fetchRecommendationsByRequirement,
  fetchRequirementsByQuestion,
} from "../../helpers/apiRequests";
import {
  Box,
  Divider,
  FormControl,
  Button,
  IconButton,
  InputLabel,
  MenuItem,
  Modal,
  Select,
  Switch,
  TextField,
  Typography,
} from "@mui/material";

const style = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  bgcolor: "background.paper",
  width: 1000,
  borderRadius: 5,
  boxShadow: 24,
  p: 2,
  overflowY: "auto",
  maxHeight: "90vh",
};

function normalizeRequirementRecommendations(requirement) {
  const recommendations = Array.isArray(requirement.recommendations)
    ? requirement.recommendations
    : [];
  const normalizedRecommendations = recommendations
    .map((rec, index) => ({
      ...rec,
      clientId: rec.clientId || rec.id || uuid(),
      level: requirement.gradations ? (rec.level ?? index) : 0,
      description: rec.description ?? "",
      recommendationText: rec.recommendationText ?? "",
    }))
    .sort((a, b) => (a.level ?? 0) - (b.level ?? 0));

  return {
    ...requirement,
    recommendations:
      normalizedRecommendations.length > 0
        ? normalizedRecommendations
        : [
            {
              clientId: uuid(),
              level: 0,
              description: "",
              recommendationText: "",
            },
          ],
  };
}

function normalizeQuestion(question) {
  return {
    ...question,
    requirements: (question.requirements || []).map((requirement) =>
      normalizeRequirementRecommendations(requirement),
    ),
  };
}

function getRecommendationsForSave(requirement) {
  const recommendations =
    normalizeRequirementRecommendations(requirement).recommendations;

  return requirement.gradations ? recommendations : recommendations.slice(0, 1);
}

export default function QuestionModal({
  open,
  setOpen,
  assessmentId,
  moduleId,
  question,
  fetchAssessmentData,
  setOpenLoading,
  setNotificationMessage,
  setOpenNotification,
}) {
  const emptyQuestion = {
    id: "",
    title: "",
    text: "",
    package: "",
    source: "",
    requirements: [],
  };

  const { t } = useTranslation();

  const [selectedPackage, setSelectedPackage] = useState(packages.BASIC);
  const [newQuestion, setNewQuestion] = useState(emptyQuestion);

  function resetData() {
    setSelectedPackage(packages.BASIC);
    setNewQuestion(emptyQuestion);
  }

  const handleQuestionDataChange = (field, value) => {
    setNewQuestion({ ...newQuestion, [field]: value });
  };

  const handleAddNewRequirement = () => {
    setNewQuestion((prev) => ({
      ...prev,
      requirements: [
        ...prev.requirements,
        {
          id: uuid(),
          text: "",
          weight: 1,
          gradations: false,
          recommendations: [
            {
              clientId: uuid(),
              level: 0,
              description: "",
              recommendationText: "",
            },
          ],
        },
      ],
    }));
  };

  const handleDeleteRequirement = (requirementId) => {
    setNewQuestion((prev) => ({
      ...prev,
      requirements: prev.requirements.filter(
        (item) => item.id !== requirementId,
      ),
    }));
  };

  const handleRequirementDataChange = (requirementId, field, value) => {
    setNewQuestion((prev) => ({
      ...prev,
      requirements: prev.requirements.map((item) =>
        item.id === requirementId ? { ...item, [field]: value } : item,
      ),
    }));
  };

  const handleRecommendationChange = (
    requirementId,
    recIndex,
    field,
    value,
  ) => {
    setNewQuestion((prev) => ({
      ...prev,
      requirements: prev.requirements.map((req) => {
        if (req.id !== requirementId) return req;
        const updatedRecs = [...req.recommendations];
        updatedRecs[recIndex] = { ...updatedRecs[recIndex], [field]: value };
        return { ...req, recommendations: updatedRecs };
      }),
    }));
  };

  const addLevel = (requirementId) => {
    setNewQuestion((prev) => ({
      ...prev,
      requirements: prev.requirements.map((req) => {
        if (req.id !== requirementId) return req;
        const nextLevel =
          Math.max(-1, ...req.recommendations.map((rec) => rec.level ?? -1)) +
          1;
        return {
          ...req,
          recommendations: [
            ...req.recommendations,
            {
              clientId: uuid(),
              level: nextLevel,
              description: "",
              recommendationText: "",
            },
          ],
        };
      }),
    }));
  };

  async function createQuestion() {
    setOpen(false);
    setOpenLoading(true);
    try {
      const assessment = await fetchAssessmentById(assessmentId);

      const questionRes = await apiClient.graphql({
        query: mutations.createQuestion,
        variables: {
          input: {
            title: newQuestion.title,
            text: newQuestion.text,
            source: newQuestion.source,
            package: selectedPackage,
            moduleID: moduleId,
            ownerGroups: assessment.ownerGroups,
          },
        },
      });
      const questionId = questionRes.data.createQuestion.id;

      await Promise.all(
        (newQuestion.requirements || []).map(async (req) => {
          const requirementRes = await apiClient.graphql({
            query: mutations.createRequirement,
            variables: {
              input: {
                questionID: questionId,
                text: req.text,
                weight: req.weight,
                gradations: !!req.gradations,
                ownerGroups: assessment.ownerGroups,
              },
            },
          });
          const requirementId = requirementRes.data.createRequirement.id;

          await Promise.all(
            getRecommendationsForSave(req).map((rec, i) => {
              const level = req.gradations ? (rec.level ?? i) : 0;
              const recommendationText =
                rec.recommendationText && rec.recommendationText !== ""
                  ? rec.recommendationText
                  : t("nothingRecommended");

              return apiClient.graphql({
                query: mutations.createRecommendation,
                variables: {
                  input: {
                    requirementID: requirementId,
                    level,
                    description: rec.description ?? "",
                    recommendationText,
                    ownerGroups: assessment.ownerGroups,
                  },
                },
              });
            }),
          );
        }),
      );

      await fetchAssessmentData();
      setNotificationMessage(`${t("question")} ${t("added")}`);
    } catch (error) {
      console.error(error);
      setNotificationMessage(t("errorMessage"));
    }
    resetData();
    setOpenNotification(true);
    setOpenLoading(false);
  }

  async function changeQuestion() {
    setOpen(false);
    setOpenLoading(true);
    try {
      const assessment = await fetchAssessmentById(assessmentId);

      await apiClient.graphql({
        query: mutations.updateQuestion,
        variables: {
          input: {
            id: newQuestion.id,
            title: newQuestion.title,
            text: newQuestion.text,
            source: newQuestion.source,
            package: selectedPackage,
          },
        },
      });

      const existingRequirementsRaw = await fetchRequirementsByQuestion(
        newQuestion.id,
      );
      const existingRequirements = await Promise.all(
        existingRequirementsRaw.map(async (req) => {
          const recsRes = await fetchRecommendationsByRequirement(req.id);
          return {
            ...req,
            recommendations: recsRes?.items || [],
          };
        }),
      );
      const existingReqMap = new Map(
        existingRequirements.map((r) => [r.id, r]),
      );

      await Promise.all(
        (newQuestion.requirements || []).map(async (req) => {
          const clientRecs = getRecommendationsForSave(req);

          if (req.id && existingReqMap.has(req.id)) {
            await apiClient.graphql({
              query: mutations.updateRequirement,
              variables: {
                input: {
                  id: req.id,
                  text: req.text,
                  weight: req.weight,
                  gradations: !!req.gradations,
                  ownerGroups: assessment.ownerGroups,
                },
              },
            });

            const existingRecs =
              existingReqMap.get(req.id).recommendations || [];
            const existingRecMap = new Map(existingRecs.map((r) => [r.id, r]));

            await Promise.all(
              clientRecs.map(async (clientRec, i) => {
                const level = req.gradations ? (clientRec.level ?? i) : 0;
                const recText =
                  clientRec.recommendationText &&
                  clientRec.recommendationText !== ""
                    ? clientRec.recommendationText
                    : t("nothingRecommended");

                if (clientRec.id && existingRecMap.has(clientRec.id)) {
                  await apiClient.graphql({
                    query: mutations.updateRecommendation,
                    variables: {
                      input: {
                        id: clientRec.id,
                        level,
                        description: clientRec.description ?? "",
                        recommendationText: recText,
                        ownerGroups: assessment.ownerGroups,
                      },
                    },
                  });
                  existingRecMap.delete(clientRec.id);
                } else {
                  await apiClient.graphql({
                    query: mutations.createRecommendation,
                    variables: {
                      input: {
                        requirementID: req.id,
                        level,
                        description: clientRec.description ?? "",
                        recommendationText: recText,
                        ownerGroups: assessment.ownerGroups,
                      },
                    },
                  });
                }
              }),
            );

            await Promise.all(
              Array.from(existingRecMap.values()).map((leftover) =>
                apiClient.graphql({
                  query: mutations.deleteRecommendation,
                  variables: { input: { id: leftover.id } },
                }),
              ),
            );

            existingReqMap.delete(req.id);
          } else {
            const createReqRes = await apiClient.graphql({
              query: mutations.createRequirement,
              variables: {
                input: {
                  questionID: newQuestion.id,
                  text: req.text,
                  weight: req.weight,
                  gradations: !!req.gradations,
                  ownerGroups: assessment.ownerGroups,
                },
              },
            });

            const createdReqId = createReqRes?.data?.createRequirement?.id;
            if (!createdReqId) return;

            await Promise.all(
              clientRecs.map((clientRec, i) => {
                const level = req.gradations ? (clientRec.level ?? i) : 0;
                const recText =
                  clientRec.recommendationText &&
                  clientRec.recommendationText !== ""
                    ? clientRec.recommendationText
                    : t("nothingRecommended");

                return apiClient.graphql({
                  query: mutations.createRecommendation,
                  variables: {
                    input: {
                      requirementID: createdReqId,
                      level,
                      description: clientRec.description ?? "",
                      recommendationText: recText,
                      ownerGroups: assessment.ownerGroups,
                    },
                  },
                });
              }),
            );
          }
        }),
      );

      await Promise.all(
        Array.from(existingReqMap.values()).map(async (leftoverReq) => {
          await Promise.all(
            (leftoverReq.recommendations || []).map((rec) =>
              apiClient.graphql({
                query: mutations.deleteRecommendation,
                variables: { input: { id: rec.id } },
              }),
            ),
          );
          await apiClient.graphql({
            query: mutations.deleteRequirement,
            variables: { input: { id: leftoverReq.id } },
          });
        }),
      );

      await fetchAssessmentData();

      setNotificationMessage(`${t("question")} ${t("updated")}`);
    } catch (err) {
      console.error(err);
      setNotificationMessage(t("errorMessage"));
    }
    resetData();
    setOpenNotification(true);
    setOpenLoading(false);
  }

  useEffect(() => {
    if (open) {
      if (question.id !== "") {
        setSelectedPackage(question.package);
        setNewQuestion(normalizeQuestion(question));
      } else {
        resetData();
      }
    } else {
      resetData();
    }
  }, [open]);

  return (
    <Modal open={open} disableAutoFocus={true}>
      <Box sx={style}>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: "row",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <Typography sx={{ fontSize: 17 }}>
              {newQuestion.id !== ""
                ? t("updateQuestion")
                : t("createNewQuestion")}
            </Typography>
            <IconButton onClick={() => setOpen(false)}>
              <CloseIcon />
            </IconButton>
          </Box>

          <Divider sx={{ my: 1 }} />

          <Box
            sx={{
              display: "flex",
              flexDirection: "row",
              alignItems: "center",
              mt: 3,
            }}
          >
            <TextField
              color="secondary"
              InputLabelProps={{ shrink: true }}
              label={t("title")}
              variant="outlined"
              size="small"
              sx={{ width: "70%" }}
              value={newQuestion.title || ""}
              onChange={(e) =>
                handleQuestionDataChange("title", e.target.value)
              }
            />
            <FormControl sx={{ width: "30%", ml: 2 }}>
              <InputLabel>{t("package")}</InputLabel>
              <Select
                label={t("package")}
                variant="outlined"
                size="small"
                value={selectedPackage}
                onChange={(e) => {
                  handleQuestionDataChange("package", e.target.value);
                  setSelectedPackage(e.target.value);
                }}
              >
                {Object.values(packages).map((p) => (
                  <MenuItem key={p} value={p}>
                    {p}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>

          <TextField
            color="secondary"
            InputLabelProps={{ shrink: true }}
            label={"Content"}
            variant="outlined"
            size="small"
            multiline
            rows={4}
            sx={{ width: 1, mt: 3 }}
            value={newQuestion.text || ""}
            onChange={(e) => handleQuestionDataChange("text", e.target.value)}
          />

          <TextField
            color="secondary"
            InputLabelProps={{ shrink: true }}
            label={t("sourceo")}
            variant="outlined"
            size="small"
            multiline
            rows={1}
            sx={{ width: 1, mt: 3 }}
            value={newQuestion.source || ""}
            onChange={(e) => handleQuestionDataChange("source", e.target.value)}
          />

          <Box
            sx={{
              display: "flex",
              flexDirection: "row",
              justifyContent: "space-between",
              alignItems: "center",
              mt: 2.5,
            }}
          >
            <Divider
              flexItem
              textAlign="left"
              sx={{
                width: 0.92,
                color: CGI_BLACK,
                userSelect: "none",
                bgcolor: "transparent",
                "&::before,&::after": { borderColor: CGI_BLACK },
              }}
            >
              {t("reqs")}
            </Divider>
            <IconButton
              sx={{ ml: 3, border: 1, width: 25, height: 25 }}
              onClick={handleAddNewRequirement}
            >
              <AddIcon />
            </IconButton>
          </Box>

          {newQuestion.requirements.length === 0 && (
            <Typography
              sx={{
                alignSelf: "center",
                fontFamily: "Inter",
                fontSize: 14,
                mb: 1,
              }}
            >
              {t("noReqs")}
            </Typography>
          )}

          {newQuestion.requirements.map((req) => (
            <Box
              key={req.id}
              sx={{
                display: "flex",
                flexDirection: "column",
                mt: 2,
                border: "1px solid #ccc",
                p: 1,
                borderRadius: 1,
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "row",
                  alignItems: "center",
                }}
              >
                <TextField
                  label={t("weight")}
                  type="number"
                  size="small"
                  value={req.weight}
                  onChange={(e) =>
                    handleRequirementDataChange(
                      req.id,
                      "weight",
                      e.target.value,
                    )
                  }
                  sx={{ maxWidth: 75, mr: 1 }}
                />
                <TextField
                  label={t("req")}
                  size="small"
                  value={req.text}
                  onChange={(e) =>
                    handleRequirementDataChange(req.id, "text", e.target.value)
                  }
                  sx={{ flex: 1, mr: 1 }}
                />
                <Typography sx={{ fontSize: 12, mr: 1 }}>
                  {t("gradations:")}
                </Typography>
                <Switch
                  checked={req.gradations || false}
                  onChange={(e) =>
                    handleRequirementDataChange(
                      req.id,
                      "gradations",
                      e.target.checked,
                    )
                  }
                  size="small"
                />
                <IconButton
                  onClick={() => handleDeleteRequirement(req.id)}
                  sx={{ ml: 1 }}
                >
                  <DeleteForeverIcon />
                </IconButton>
              </Box>
              {req.gradations ? (
                req.recommendations.map((rec, index) => (
                  <RecommendationInputField
                    key={rec.id || rec.clientId}
                    requirement={req}
                    recIndex={index}
                    updateRequirement={handleRecommendationChange}
                  />
                ))
              ) : (
                <Box sx={{ mt: 1 }}>
                  <TextField
                    label={t("recommendation")}
                    size="small"
                    multiline
                    rows={2}
                    value={req.recommendations[0]?.recommendationText || ""}
                    onChange={(e) =>
                      handleRecommendationChange(
                        req.id,
                        0,
                        "recommendationText",
                        e.target.value,
                      )
                    }
                    sx={{ width: "100%" }}
                  />
                </Box>
              )}

              {req.gradations && (
                <Button sx={{ mt: 1 }} onClick={() => addLevel(req.id)}>
                  {t("addRecommendation")}
                </Button>
              )}
            </Box>
          ))}

          <Box
            sx={{
              display: "flex",
              flexDirection: "row",
              justifyContent: "flex-end",
              mt: 2,
            }}
          >
            <IconButton
              sx={{ mt: 1 }}
              onClick={() =>
                newQuestion.id !== "" ? changeQuestion() : createQuestion()
              }
            >
              <CheckIcon />
            </IconButton>
          </Box>
        </Box>
      </Box>
    </Modal>
  );
}

// RecommendationInputField bleibt unverändert
function RecommendationInputField({
  requirement,
  recIndex,
  updateRequirement,
}) {
  const { t } = useTranslation();
  const rec = requirement.recommendations[recIndex];

  const handleChange = (field, value) => {
    updateRequirement(requirement.id, recIndex, field, value);
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", mt: 1, ml: 2 }}>
      <Typography sx={{ fontSize: 12 }}>Level {recIndex}</Typography>
      <TextField
        label={t("description")}
        size="small"
        multiline
        rows={2}
        value={rec.description}
        onChange={(e) => handleChange("description", e.target.value)}
        sx={{ mb: 0.5 }}
      />
      <TextField
        label={t("recommendation")}
        size="small"
        multiline
        rows={2}
        value={rec.recommendationText}
        onChange={(e) => handleChange("recommendationText", e.target.value)}
      />
    </Box>
  );
}
